import * as Sentry from "@sentry/vue";

type ApiErrorBody = {
  code?: unknown;
  message?: unknown;
  error?: unknown;
  category?: unknown;
  details?: unknown;
};

export type ApiErrorResponse = {
  status: number;
  data?: ApiErrorBody | string;
};

const expectedCategories = new Set([
  "validation",
  "auth",
  "permission",
  "domain",
  "rate_limit",
  "security",
]);

export function isApiErrorResponse(error: unknown): error is ApiErrorResponse {
  return (
    !!error && typeof error === "object" && "status" in error && typeof error.status === "number"
  );
}

export function getApiErrorCategory(error: unknown): string | null {
  if (!isApiErrorResponse(error) || !error.data || typeof error.data !== "object") return null;
  return typeof error.data.category === "string" ? error.data.category : null;
}

export function isExpectedApiRejection(error: unknown): boolean {
  if (!isApiErrorResponse(error)) return false;
  if (error.status >= 500) return false;

  const category = getApiErrorCategory(error);
  return category ? expectedCategories.has(category) : error.status >= 400 && error.status < 500;
}

export function isCapturedApiFailure(error: unknown): boolean {
  if (!isApiErrorResponse(error)) return true;
  return error.status >= 500;
}

export function getApiErrorMessage(error: unknown, fallback = "Something went wrong."): string {
  if (!isApiErrorResponse(error)) {
    return error instanceof Error ? error.message : fallback;
  }

  const data = error.data;
  if (typeof data === "string" && data.trim()) return data;
  if (!data || typeof data !== "object") return fallback;

  if (typeof data.message === "string" && data.message.trim()) return data.message;
  if (typeof data.error === "string" && data.error.trim()) return data.error;

  return fallback;
}

export function captureUnexpectedApiError(error: unknown, context?: Record<string, unknown>) {
  if (isExpectedApiRejection(error)) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}
